import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';

const vitaminCRange = [
  {
    name: 'Vitamin C 10%',
    role: 'ANTIOXIDANT BRIGHTENING',
    desc: 'Helps brighten dull-looking skin, improve radiance and support a more even skin tone for a healthy glow.',
    bestFor: 'Dullness, Uneven Tone, Pigmentation',
  },
  {
    name: 'Niacinamide',
    role: 'BARRIER + TEXTURE SUPPORT',
    desc: 'Helps improve skin texture, minimize the appearance of pores and support a balanced skin barrier.',
    bestFor: 'Large Pores, Oiliness, Uneven Texture',
  },
  {
    name: 'Hyaluronic Acid',
    role: 'DEEP HYDRATION',
    desc: 'Attracts and retains moisture to keep skin hydrated, plump and comfortable throughout the day.',
    bestFor: 'Dryness, Dehydration, Tight Skin',
  },
  {
    name: 'Alpha Arbutin',
    role: 'DARK SPOT SUPPORT',
    desc: 'Helps reduce the appearance of dark spots and promotes a more even-looking complexion.',
    bestFor: 'Pigmentation, Post-Acne Marks',
  },
  {
    name: 'Centella Asiatica',
    role: 'SKIN SOOTHING',
    desc: 'Known for its calming properties that help soothe stressed and irritated skin.',
    bestFor: 'Sensitive Skin, Redness',
  },
];

const salicylicRange = [
  {
    name: 'Salicylic Acid 2%',
    role: 'PORE CLARIFYING ACTIVE',
    desc: 'Oil-soluble exfoliant that helps unclog pores, reduce blackheads and minimize breakouts.',
    bestFor: 'Acne-Prone Skin, Clogged Pores',
  },
  {
    name: 'Niacinamide',
    role: 'OIL BALANCING',
    desc: 'Helps regulate excess oil and improve the appearance of enlarged pores.',
    bestFor: 'Oily Skin, Visible Pores',
  },
  {
    name: 'Hyaluronic Acid',
    role: 'LIGHTWEIGHT HYDRATION',
    desc: 'Provides hydration without feeling heavy or greasy on the skin.',
    bestFor: 'Dehydrated Oily Skin',
  },
  {
    name: 'Willow Bark Extract',
    role: 'GENTLE EXFOLIATION',
    desc: 'Natural source of salicylates that supports smoother and clearer-looking skin.',
    bestFor: 'Congested Skin, Rough Texture',
  },
  {
    name: 'Green Tea Extract',
    role: 'ANTIOXIDANT PROTECTION',
    desc: 'Rich in antioxidants that help protect skin from environmental stressors.',
    bestFor: 'Acne-Prone Skin, Irritated Skin',
  },
];

const fullIngredients = [
  {
    label: 'Vitamin C Serum – Full Ingredients',
    content:
      'Aqua, Ethyl Ascorbic Acid, Niacinamide, Alpha-Arbutin, Tranexamic Acid, Glutathione, Hyaluronic Acid, Centella Extract, Aminopropyl Ascorbyl Phosphate, Propylene Glycol, Acacia Senegal Gum, Xanthan Gum, Centella Extract, Geranium Robertianum Extract, Disodium EDTA, Thymus Serpyllum Extract, Alpinia Katsumadai Seed Extract, Phenoxyethanol, Ethylhexylglycerin.',
  },
  {
    label: 'Salicylic Acid Serum – Full Ingredients',
    content:
      'Aqua, Salicylic Acid, Niacinamide, Hyaluronic Acid, Urea, Glycine Soja Extract, Phenoxyethanol, Ethylhexylglycerin, Acacia Senegal Gum, Xanthan Gum, Propylene Glycol, Glycerin, Fructose, Citric Acid, Sodium Hydroxide, Maltose, Sodium PCA, Sodium Chloride, Sodium Lactate, Trehalose, Allantoin, Sodium Hyaluronate, Glucose, Green Tea Extract, Houttuynia Cordata Extract, Scutellaria Baicalensis Root Extract, Neem Leaf Extract, Rehmannia Chinensis Root Extract, Willow Bark Extract, Phellodendron Bark Extract, Lactobacillus Pear Juice Ferment Filtrate.',
  },
];

function IngredientCard({ item }) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-[#fdf8f6] p-7 transition-colors hover:border-black">
      <p className="mb-2 text-[11px] uppercase tracking-[0.25em] text-[#cc7c66]">{item.role}</p>
      <h3 className="mb-3 font-display text-3xl font-light leading-tight text-black">{item.name}</h3>
      <p className="mb-5 text-sm font-light leading-relaxed text-gray-600">{item.desc}</p>
      <p className="text-xs uppercase tracking-[0.18em] text-gray-500">
        Best for: <span className="normal-case tracking-normal text-gray-700">{item.bestFor}</span>
      </p>
    </article>
  );
}

export default function Ingredients() {
  const [openIngredient, setOpenIngredient] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen">
      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden bg-black text-white">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 40%, #cc7c66 0%, transparent 58%)' }}
        />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 lg:grid-cols-2 lg:items-end lg:px-8 lg:py-24">
          <div>
            <p className="mb-5 text-xs uppercase tracking-[0.35em] text-[#cc7c66]">Ingredients</p>
            <h1 className="font-display text-5xl font-light leading-tight sm:text-6xl lg:text-7xl">
              Every active.
              <br />
              Every purpose.
              <br />
              <em className="not-italic text-[#f5e6e0]">Zero guesswork.</em>
            </h1>
          </div>
          <p className="max-w-xl text-sm font-light leading-relaxed text-gray-300 sm:text-base">
            We build formulas with clinically supported actives at functional concentrations.
            No decorative ingredients, no vague labels, and no confusing jargon. Just a clear
            explanation of what each ingredient does for your skin.
          </p>
        </div>
      </section>

      {/* ─── VITAMIN C RANGE ─── */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="mb-2 text-center text-xs uppercase tracking-[0.3em] text-gray-400">Vitamin C Range</p>
          <h2 className="mb-12 text-center font-display text-4xl font-light sm:text-5xl">Antioxidant Brightening</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {vitaminCRange.map((item) => (
              <IngredientCard key={item.name} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── SALICYLIC ACID RANGE ─── */}
      <section className="bg-[#fdf8f6] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="mb-2 text-center text-xs uppercase tracking-[0.3em] text-gray-400">Salicylic Acid Range</p>
          <h2 className="mb-12 text-center font-display text-4xl font-light sm:text-5xl">Pore Clarifying Active</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {salicylicRange.map((item) => (
              <IngredientCard key={item.name} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── VIEW FULL INGREDIENT LIST ─── */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <button
            onClick={() => setOpenIngredient(openIngredient ? null : 'open')}
            className="inline-flex items-center gap-2 border border-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-black hover:text-white transition-colors"
          >
            VIEW FULL INGREDIENT LIST <ChevronDown size={14} className={`transition-transform ${openIngredient ? 'rotate-180' : ''}`} />
          </button>

          {openIngredient && (
            <div className="mt-8 space-y-6 text-left">
              {fullIngredients.map((item) => (
                <div key={item.label} className="border border-gray-200 p-6">
                  <p className="mb-3 text-xs uppercase tracking-[0.2em] text-gray-500 font-medium">{item.label}</p>
                  <p className="text-sm font-light leading-relaxed text-gray-600">{item.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ─── NO FLUFF SECTION ─── */}
      <section className="bg-[#fdf8f6] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="mb-2 text-center text-xs uppercase tracking-[0.3em] text-[#cc7c66]">What We Leave Out</p>
          <h2 className="mb-12 text-center font-display text-4xl font-light sm:text-5xl">No fluff, only function</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: 'No Added Parabens',
                desc: 'We formulate without added parabens and focus on ingredients that support product performance and stability.',
              },
              {
                title: 'No Hidden Fragrance Blends',
                desc: 'No unnecessary fragrance blends that may cause irritation for sensitive skin types.',
              },
              {
                title: 'No Mineral Oil-Heavy Fillers',
                desc: 'Every ingredient is selected with purpose, avoiding heavy filler-based formulations.',
              },
              {
                title: 'No Unnecessary Colorants',
                desc: 'No added colors or dyes used solely for appearance.',
              },
              {
                title: 'No False Promises',
                desc: 'Transparent formulations backed by carefully selected active ingredients.',
              },
              {
                title: 'No Overcomplicated Routines',
                desc: 'Simple, effective skincare designed around essential actives.',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl border border-gray-200 bg-white p-7 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#fdf8f6]">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cc7c66" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                  </svg>
                </div>
                <h3 className="mb-2 font-display text-lg font-medium">{item.title}</h3>
                <p className="text-sm font-light leading-relaxed text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ROUTINE GUIDE ─── */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="mb-2 text-center text-xs uppercase tracking-[0.3em] text-gray-400">How To Build Your Routine</p>
          <h2 className="mb-12 text-center font-display text-4xl font-light sm:text-5xl">Simple routine guide</h2>
          <p className="mx-auto mb-10 max-w-2xl text-center text-sm font-light text-gray-500">
            Powered by two essential actives for clearer, brighter skin.
          </p>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Morning */}
            <div className="rounded-2xl border border-gray-200 bg-[#fdf8f6] p-8">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-white">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cc7c66" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2" />
                  <path d="M12 20v2" />
                  <path d="m4.93 4.93 1.41 1.41" />
                  <path d="m17.49 17.49 1.41 1.41" />
                  <path d="M2 12h2" />
                  <path d="M20 12h2" />
                  <path d="m6.34 17.66-1.41 1.41" />
                  <path d="m19.07 4.93-1.41 1.41" />
                </svg>
              </div>
              <p className="mb-2 text-center text-[11px] uppercase tracking-[0.25em] text-[#cc7c66]">Morning Routine</p>
              <h3 className="mb-6 text-center font-display text-xl font-light">Start your day bright</h3>
              <div className="space-y-5">
                {[
                  { step: '1. Cleanse', desc: 'Start with a gentle cleanser to remove dirt and oil.' },
                  { step: '2. Vitamin C Serum', desc: 'Apply 2–3 drops to face and neck. Helps brighten and protect.' },
                  { step: '3. Moisturizer', desc: 'Lock in hydration and support your skin barrier.' },
                  { step: '4. Sunscreen (SPF 30+)', desc: 'Always finish with sunscreen to protect from UV damage.' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-3">
                    <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                        <path d="M12 2v4" />
                        <path d="m16.2 7.8 2.9-2.9" />
                        <path d="M18 12h4" />
                        <path d="m16.2 16.2 2.9 2.9" />
                        <path d="M12 18v4" />
                        <path d="m4.9 19.1 2.9-2.9" />
                        <path d="M2 12h4" />
                        <path d="m4.9 4.9 2.9 2.9" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{item.step}</p>
                      <p className="text-xs font-light text-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Evening */}
            <div className="rounded-2xl border border-gray-200 bg-[#fdf8f6] p-8">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-white">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cc7c66" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                </svg>
              </div>
              <p className="mb-2 text-center text-[11px] uppercase tracking-[0.25em] text-[#cc7c66]">Evening Routine</p>
              <h3 className="mb-6 text-center font-display text-xl font-light">Repair while you sleep</h3>
              <div className="space-y-5">
                {[
                  { step: '1. Cleanse', desc: 'Remove makeup, sunscreen and impurities.' },
                  { step: '2. Salicylic Acid Serum', desc: 'Apply 2–3 drops to clean, dry skin. Focus on acne-prone areas.' },
                  { step: '3. Moisturizer', desc: 'Follow with moisturizer to keep skin hydrated.' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-3">
                    <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                        <path d="M12 3v4" />
                        <path d="m16.2 7.8 2.9-2.9" />
                        <path d="M18 12h4" />
                        <path d="m16.2 16.2 2.9 2.9" />
                        <path d="M12 18v4" />
                        <path d="m4.9 19.1 2.9-2.9" />
                        <path d="M2 12h4" />
                        <path d="m4.9 4.9 2.9 2.9" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{item.step}</p>
                      <p className="text-xs font-light text-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Best Results */}
            <div className="rounded-2xl border border-gray-200 bg-[#fdf8f6] p-8">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-white">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cc7c66" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                  <path d="M19 3v4" />
                  <path d="M21 5h-4" />
                </svg>
              </div>
              <p className="mb-2 text-center text-[11px] uppercase tracking-[0.25em] text-[#cc7c66]">For Best Results</p>
              <h3 className="mb-6 text-center font-display text-xl font-light">Tips that matter</h3>
              <div className="space-y-5">
                {[
                  { step: 'Be consistent', desc: 'Use daily for at least 6–8 weeks for visible results.' },
                  { step: "Don't skip sunscreen", desc: 'Vitamin C works best when protected with SPF.' },
                  { step: 'Start slow', desc: 'If using Salicylic Acid for the first time, start 2–3 times a week and increase as tolerated.' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-3">
                    <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                        <path d="M12 3v4" />
                        <path d="m16.2 7.8 2.9-2.9" />
                        <path d="M18 12h4" />
                        <path d="m16.2 16.2 2.9 2.9" />
                        <path d="M12 18v4" />
                        <path d="m4.9 19.1 2.9-2.9" />
                        <path d="M2 12h4" />
                        <path d="m4.9 4.9 2.9 2.9" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{item.step}</p>
                      <p className="text-xs font-light text-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="bg-black py-20 text-white">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#cc7c66]">Put it into practice</p>
          <h2 className="mb-6 font-display text-4xl font-light sm:text-5xl">
            Explore products built with these ingredients
          </h2>
          <p className="mx-auto mb-9 max-w-2xl text-sm font-light leading-relaxed text-gray-300">
            Every formula is designed around a specific skin need — no fluff, no fillers.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 border border-white/40 px-8 py-4 text-xs uppercase tracking-[0.25em] transition-colors hover:bg-white hover:text-black"
          >
            Shop Products <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}